import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ShowcaseState,
  ShowcaseApiError,
  getShowcase,
  activateIntro,
  activateConsent,
  activateChoosing,
  disableShowcase,
  enableShowcase,
  updateShowcaseSettings,
  ConsentPayload,
  ChoosingPayload,
  UpdateShowcaseSettingsPayload,
} from '@/api/showcase'

// Dedup in-flight запросов по channelUUID — useShowcase вызывается из
// нескольких мест (ShowcaseRoute + OverviewPage + SettingsPage), и без дедупа
// они делали параллельные fetch'и (см. логи 16.06: 2 запроса с интервалом 50ms).
const inflight = new Map<string, Promise<ShowcaseState>>()
function dedupedGetShowcase(channelUUID: string): Promise<ShowcaseState> {
  const existing = inflight.get(channelUUID)
  if (existing) return existing
  const p = getShowcase(channelUUID).finally(() => inflight.delete(channelUUID))
  inflight.set(channelUUID, p)
  return p
}

/**
 * Единая точка работы с модулем Витрина: загрузка state + actions + опциональный polling.
 *
 * Polling включается автоматически в `processing` (~5-14 дней до active), чтобы UI
 * сам переключился на дашборд когда страницы реально появятся в индексе.
 */
export function useShowcase(channelUUID: string | undefined) {
  const [state, setState] = useState<ShowcaseState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ShowcaseApiError | null>(null)
  const pollingRef = useRef<number | null>(null)

  const refetch = useCallback(async () => {
    if (!channelUUID) return
    try {
      const res = await dedupedGetShowcase(channelUUID)
      setState(res)
      setError(null)
    } catch (e) {
      setError(e instanceof ShowcaseApiError ? e : new ShowcaseApiError('UNKNOWN', 500, ''))
    } finally {
      setLoading(false)
    }
  }, [channelUUID])

  // Первичная загрузка + перезапрос при смене канала
  useEffect(() => {
    if (!channelUUID) {
      setLoading(false)
      return
    }
    setLoading(true)
    refetch()
  }, [channelUUID, refetch])

  // Polling в processing — раз в 10с до перехода в active
  useEffect(() => {
    if (state?.status !== 'processing' || !channelUUID) {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      return
    }
    pollingRef.current = window.setInterval(refetch, 10_000)
    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [state?.status, channelUUID, refetch])

  const doActivateIntro = useCallback(async () => {
    if (!channelUUID) return
    const next = await activateIntro(channelUUID)
    setState(next)
    return next
  }, [channelUUID])

  const doActivateConsent = useCallback(async (payload: ConsentPayload) => {
    if (!channelUUID) return
    const next = await activateConsent(channelUUID, payload)
    setState(next)
    return next
  }, [channelUUID])

  const doActivateChoosing = useCallback(async (payload: ChoosingPayload) => {
    if (!channelUUID) return
    const next = await activateChoosing(channelUUID, payload)
    setState(next)
    return next
  }, [channelUUID])

  const doDisable = useCallback(async () => {
    if (!channelUUID) return
    const next = await disableShowcase(channelUUID)
    setState(next)
    return next
  }, [channelUUID])

  const doUpdateSettings = useCallback(async (payload: UpdateShowcaseSettingsPayload) => {
    if (!channelUUID) return
    const next = await updateShowcaseSettings(channelUUID, payload)
    setState(next)
    return next
  }, [channelUUID])

  const doEnable = useCallback(async () => {
    if (!channelUUID) return
    const next = await enableShowcase(channelUUID)
    setState(next)
    return next
  }, [channelUUID])

  return {
    state,
    loading,
    error,
    refetch,
    actions: {
      activateIntro: doActivateIntro,
      activateConsent: doActivateConsent,
      activateChoosing: doActivateChoosing,
      updateSettings: doUpdateSettings,
      disable: doDisable,
      enable: doEnable,
    },
  }
}
